import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';

const TermsPageEn = () => {
  return (
    <>
      <SeoMeta
        title="Terms of Use | BlueCMR"
        description="General Terms of Use of the BlueCMR platform for digital transport documents: eCMR, consignment notes and control documents (DeCA)."
        canonical="https://www.bluecmr.com/en/terms"
        locale="en_GB"
      />

      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              BlueCMR General Terms of Use
            </h1>

            <p className="text-sm text-gray-600 mb-4">
              This English version is provided for information purposes. In the event of any discrepancy, the{' '}
              <Link to="/terminos" className="text-blue-600 hover:underline">Spanish version</Link> shall prevail.
            </p>

            <p className="text-sm text-gray-600 mb-8">
              Last updated: 22 April 2026
            </p>

            {/* 1. Identification of the service provider */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Identification of the service provider
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR is a technology platform operated by <strong>BlueCMR S.L.U.</strong>, with tax identification number (NIF) <strong>B26654517</strong> and registered office at <strong>Calle Fueros de León 1, 24402, Ponferrada, León, Spain</strong> (hereinafter, "BlueCMR").
              </p>
              <p className="text-gray-700">
                For any enquiry relating to these Terms, BlueCMR may be contacted at: <strong>support@bluecmr.com</strong>.
              </p>
            </section>

            {/* 2. Definitions */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Definitions
              </h2>
              <p className="text-gray-700 mb-2">For the purposes of these Terms:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Platform:</strong> the BlueCMR platform, accessible via the web, a mobile application or any other interface enabled by BlueCMR.</li>
                <li><strong>Client:</strong> the legal entity or professional that contracts or uses BlueCMR for its business or professional activities.</li>
                <li><strong>Authorised User:</strong> any natural person authorised by the Client to access or use the Platform on the Client's behalf.</li>
                <li><strong>Services:</strong> the services provided by BlueCMR through the Platform, including, among others, the digital management of transport documents, evidence, electronic signature and associated functionalities.</li>
                <li><strong>Client Content:</strong> the data, documents, images, signatures, evidence and other information uploaded, generated, transmitted or managed by the Client or its Authorised Users through the Platform.</li>
              </ul>
            </section>

            {/* 3. Purpose and scope */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. Purpose and scope
              </h2>
              <p className="text-gray-700 mb-4">
                These Terms govern access to, registration on, browsing of and use of the Platform and the Services offered by BlueCMR.
              </p>
              <p className="text-gray-700 mb-4">
                These Terms apply to any form of use of BlueCMR, except for matters expressly governed by a contract, order, accepted proposal, subscription agreement, financial annex or special terms and conditions signed between BlueCMR and the Client. In the event of any conflict, such specific documents shall prevail over these Terms.
              </p>
              <p className="text-gray-700">
                The use of BlueCMR is aimed primarily at business or professional clients. Where an Authorised User acts on behalf of an organisation, they represent and warrant that they have sufficient authority to bind that organisation to the extent applicable.
              </p>
            </section>

            {/* 4. Acceptance of the Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Acceptance of the Terms
              </h2>
              <p className="text-gray-700 mb-4">
                Access to or use of the Platform requires acceptance of these Terms, as well as of any other contractual documents or policies that may apply, including, where appropriate:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>the Privacy Policy;</li>
                <li>the Data Processing Agreement or DPA, where BlueCMR processes personal data on behalf of the Client;</li>
                <li>the special or commercial terms and conditions applicable to the contracted service.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Acceptance may be given by signature, clickwrap, ticking a checkbox, electronic acceptance, continued use of the Platform or any other valid mechanism for expressing consent.
              </p>
              <p className="text-gray-700">
                If the Authorised User does not accept these Terms, they must not use the Platform.
              </p>
            </section>

            {/* 5. Registration, accounts and access */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Registration, accounts and access
              </h2>
              <p className="text-gray-700 mb-4">
                In order to access certain functionalities, BlueCMR may require the prior registration of an account.
              </p>
              <p className="text-gray-700 mb-2">The Client and its Authorised Users undertake to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>provide truthful, accurate and up-to-date information;</li>
                <li>keep the account details up to date;</li>
                <li>safeguard their credentials diligently;</li>
                <li>not share access in an unauthorised manner;</li>
                <li>notify without delay any unauthorised use of, or security incident relating to, the account.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                The Client shall be responsible for the administration of its Authorised Users and for the use of the Platform made from its accounts, without prejudice to any individual liability that may attach to each Authorised User.
              </p>
              <p className="text-gray-700">
                BlueCMR may suspend or cancel accounts where it detects reasonable indications of use that is unlawful, fraudulent, insecure, contrary to these Terms or contrary to applicable regulations.
              </p>
            </section>

            {/* 6. Permitted use and restrictions */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Permitted use and restrictions
              </h2>
              <p className="text-gray-700 mb-4">
                The Client and its Authorised Users undertake to use BlueCMR in accordance with the law, good faith, public order and these Terms.
              </p>
              <p className="text-gray-700 mb-2">The following, among others, are prohibited:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>using the Platform for unlawful, fraudulent or unauthorised purposes;</li>
                <li>entering false or misleading data;</li>
                <li>reselling, sublicensing, commercially exploiting or making the Platform available to third parties without the express authorisation of BlueCMR;</li>
                <li>attempting to access systems, accounts, environments or data of third parties without authorisation;</li>
                <li>reverse engineering, decompiling, disassembling or attempting to extract the source code of the Platform, except where permitted by law;</li>
                <li>introducing malware, viruses or any element intended to alter, damage or compromise the security or availability of the service;</li>
                <li>deliberately interfering with or overloading BlueCMR's infrastructure;</li>
                <li>using the Platform in a manner that infringes the rights of third parties, including intellectual property, trade secrets or data protection regulations.</li>
              </ul>
              <p className="text-gray-700">
                The Client shall be responsible for the use of BlueCMR by its Authorised Users, and for ensuring that such use complies with its internal policies and applicable regulations.
              </p>
            </section>

            {/* 7. Client Content */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                7. Client Content
              </h2>
              <p className="text-gray-700 mb-4">
                The Client retains ownership of its Client Content.
              </p>
              <p className="text-gray-700 mb-2">The Client represents and warrants that:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>it holds all the rights, titles, authorisations and legal bases required in respect of the Client Content;</li>
                <li>such content is lawful and does not infringe the rights of third parties;</li>
                <li>the personal data included in such content have been processed and incorporated into BlueCMR in compliance with applicable regulations.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                BlueCMR does not acquire any ownership rights over the Client Content. However, the Client grants BlueCMR a non-exclusive, limited, royalty-free licence, for the term of the contractual relationship, to host, reproduce, process, communicate and make available such content to the extent necessary to provide the Services.
              </p>
              <p className="text-gray-700">
                BlueCMR does not proactively monitor all Client Content, but may remove, block or restrict access to content where it has reasonable indications of unlawfulness, a security risk or a breach of these Terms.
              </p>
            </section>

            {/* 8. Electronic signature and evidence */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                8. Electronic signature and evidence
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR may make available to the Client various electronic signature and evidence-generation functionalities, including, among others, digitised handwritten signature, OTP, biometric signature, advanced electronic signature, delegated signature, automatic signature, electronic seal and other technically available methods.
              </p>
              <p className="text-gray-700 mb-2">The Client expressly acknowledges and accepts that:</p>
              <ol className="list-[lower-alpha] pl-6 space-y-3 text-gray-700 mb-4">
                <li>any signature made through the Platform is made on behalf of and under the responsibility of the Client or of the entity represented by the relevant Authorised User;</li>
                <li>it is solely the Client's responsibility to verify the identity, entitlement, capacity, representation, powers and internal authorisations of the persons who sign or take part in documents managed through the Platform;</li>
                <li>BlueCMR acts solely as a technology provider and does not itself validate the legal capacity, powers of representation or substantive entitlement of signatories;</li>
                <li>the use of delegated signature, automatic signature or electronic seal functionalities requires the Client to hold the necessary authorisations and to assume full responsibility for their activation and use;</li>
                <li>BlueCMR does not assume the role of signatory, representative, agent, contracting party or substantive participant in the signed document, even where it provides technical means, evidence or sealing mechanisms;</li>
                <li>the validity, effectiveness, enforceability against third parties or recognition of a signed document will also depend on the correct use of the Platform, the configuration chosen, the applicable regulatory context and the relationships between the parties involved.</li>
              </ol>
              <p className="text-gray-700">
                Any dispute concerning the signatory's entitlement, the internal validity of the document, the authorisation to sign or the legal effects of the document shall be the responsibility of the Client, unless it arises directly from a breach attributable to BlueCMR in the provision of the Service.
              </p>
            </section>

            {/* 9. Nature of the service */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                9. Nature of the service
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR is a technology and document platform. BlueCMR does not act as carrier, logistics operator, shipper, consignee or consignor, nor as substantive representative of the parties involved in a transport operation, unless expressly agreed otherwise in writing.
              </p>
              <p className="text-gray-700 mb-4">
                The Platform facilitates the management, creation, exchange, storage, consultation and traceability of electronic documents and evidence, but does not in itself replace the legal or contractual obligations of the Client or of third parties involved in the operation.
              </p>
              <p className="text-gray-700 mb-2">The Client shall remain solely responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>the accuracy, completeness and lawfulness of the data and documents entered;</li>
                <li>compliance with the legal and regulatory obligations applicable to its business;</li>
                <li>the underlying relationships with third parties involved in the logistics chain;</li>
                <li>verifying whether the use of electronic documents is sufficient or appropriate in each specific case.</li>
              </ul>
            </section>

            {/* 10. Availability, support and changes to the service */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                10. Availability, support and changes to the service
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR will make reasonable efforts to maintain the availability and regular operation of the Platform, without guaranteeing uninterrupted or error-free availability.
              </p>
              <p className="text-gray-700 mb-2">The Service may be affected by:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>scheduled maintenance;</li>
                <li>updates;</li>
                <li>technical incidents;</li>
                <li>outages of third-party providers;</li>
                <li>force majeure;</li>
                <li>or any other circumstance reasonably beyond BlueCMR's control.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                BlueCMR may modify, update, replace or discontinue functionalities of the Platform for technical, operational, legal, regulatory or business reasons. Where such changes materially affect the contracted Service, BlueCMR will notify the Client by reasonable means and sufficiently in advance.
              </p>
              <p className="text-gray-700">
                Unless expressly agreed otherwise, BlueCMR does not assume any specific service level obligations or SLAs beyond those arising from reasonable professional diligence in the provision of the Service.
              </p>
            </section>

            {/* 11. Intellectual and industrial property */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                11. Intellectual and industrial property
              </h2>
              <p className="text-gray-700 mb-4">
                All intellectual and industrial property rights in the Platform, its software, design, structure, interfaces, documentation, trademarks, trade names, logos, databases, functionalities, developments, know-how and other elements of the Service belong to BlueCMR or its licensors.
              </p>
              <p className="text-gray-700 mb-4">
                Use of the Platform does not entail any assignment of rights to the Client or to the Authorised Users.
              </p>
              <p className="text-gray-700">
                BlueCMR grants the Client, for the term of the contractual relationship, a limited, non-exclusive, revocable, non-sublicensable and non-transferable licence to access and use the Platform in accordance with these Terms and, where appropriate, with the applicable contractual documents.
              </p>
            </section>

            {/* 12. Personal data protection */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                12. Personal data protection
              </h2>
              <p className="text-gray-700 mb-4">
                The processing of personal data by BlueCMR shall be governed by applicable regulations and by the corresponding contractual documentation.
              </p>
              <p className="text-gray-700 mb-4">
                Where BlueCMR processes personal data on behalf of the Client in the course of providing the Services, it shall act as data processor under the terms set out in the applicable DPA.
              </p>
              <p className="text-gray-700 mb-4">
                Information on the processing of personal data relating to contacts, account users, representatives or website visitors is set out in BlueCMR's Privacy Policy.
              </p>
              <p className="text-gray-700">
                The Client warrants that it has a sufficient legal basis for processing, and disclosing to BlueCMR, the personal data incorporated into the Platform.
              </p>
            </section>

            {/* 13. Confidentiality */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                13. Confidentiality
              </h2>
              <p className="text-gray-700 mb-4">
                Each party undertakes to keep confidential any non-public information of the other party to which it gains access in connection with the contractual relationship, and to use such information solely for the performance of that relationship.
              </p>
              <p className="text-gray-700 mb-2">Information shall not be regarded as confidential if it:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>is or becomes public without breach of this obligation;</li>
                <li>was already lawfully in the possession of the receiving party;</li>
                <li>must be disclosed pursuant to a legal, judicial or administrative obligation;</li>
                <li>has been lawfully obtained from a third party not bound by a duty of confidentiality.</li>
              </ul>
              <p className="text-gray-700">
                This obligation shall remain in force for the duration of the contractual relationship and for five (5) years after its termination, unless by its nature it must be maintained for longer.
              </p>
            </section>

            {/* 14. Financial terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                14. Financial terms
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR's Services may be free of charge or paid.
              </p>
              <p className="text-gray-700 mb-4">
                Where the Service is paid, the applicable financial terms shall be those set out in the accepted commercial proposal, subscription agreement, order, financial annex or equivalent contractual documentation.
              </p>
              <p className="text-gray-700 mb-2">Unless expressly agreed otherwise in writing:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>amounts are stated exclusive of indirect taxes;</li>
                <li>the Client shall pay the amounts invoiced within the time limits indicated in the applicable invoice or contract;</li>
                <li>late payments may accrue interest and may give rise to suspension of the Service after reasonable notice.</li>
              </ul>
              <p className="text-gray-700">
                BlueCMR may update its rates for new contracts, renewals or subsequent periods, or during the term of the relationship where this is expressly permitted by the applicable contractual documents and notified reasonably in advance.
              </p>
            </section>

            {/* 15. Suspension */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                15. Suspension
              </h2>
              <p className="text-gray-700 mb-2">BlueCMR may suspend access to the Platform, in whole or in part, where:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>there is non-payment;</li>
                <li>there is a reasonable risk to the security, integrity or availability of the Service;</li>
                <li>it detects use that is unlawful, fraudulent or contrary to these Terms;</li>
                <li>there is a legal requirement or a requirement from a competent authority;</li>
                <li>this is necessary to carry out urgent maintenance or to mitigate an incident.</li>
              </ul>
              <p className="text-gray-700">
                Wherever reasonably possible, BlueCMR will inform the Client in advance of the suspension and of the reasons for it.
              </p>
            </section>

            {/* 16. Disclaimer of warranties */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                16. Disclaimer of warranties
              </h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, the Platform is provided "as is" and "as available".
              </p>
              <p className="text-gray-700 mb-2">BlueCMR gives no express or implied warranties regarding:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>uninterrupted availability;</li>
                <li>the complete absence of errors;</li>
                <li>fitness for a particular purpose not expressly agreed;</li>
                <li>automatic compliance with the Client's specific regulatory requirements;</li>
                <li>universal recognition of documents or signatures by third parties;</li>
                <li>or specific results arising from the use of the Platform.</li>
              </ul>
              <p className="text-gray-700">
                BlueCMR does not itself guarantee that any document generated or signed through the Platform will automatically be valid, effective or accepted in any jurisdiction or proceeding, by any authority or in any relationship with third parties. It is the Client's responsibility to verify that its use is appropriate to the applicable regulatory and operational framework.
              </p>
            </section>

            {/* 17. Limitation of liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                17. Limitation of liability
              </h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, BlueCMR shall not be liable for any indirect, special, incidental, punitive or consequential damages, nor for loss of profits, loss of revenue, loss of business, loss of reputation, loss of data or business interruption.
              </p>
              <p className="text-gray-700 mb-4">
                BlueCMR's total aggregate liability arising out of or in connection with the Platform or the Services shall be limited to the amount actually paid by the Client to BlueCMR in the twelve (12) months preceding the event giving rise to the claim.
              </p>
              <p className="text-gray-700 mb-4">
                The foregoing exclusions and limitations shall not apply in cases where liability cannot validly be excluded or limited by law, nor in cases of wilful misconduct or gross negligence where applicable regulations prevent such limitation.
              </p>
              <p className="text-gray-700 mb-2">BlueCMR shall not be liable for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>the underlying relationship between the parties involved in the transport operation;</li>
                <li>the content entered by the Client;</li>
                <li>the entitlement or powers of signatories;</li>
                <li>the Client's failure to comply with its legal or contractual obligations;</li>
                <li>incidents attributable to third-party providers, telecommunications networks or the Client's systems, beyond BlueCMR's reasonable control.</li>
              </ul>
            </section>

            {/* 18. Indemnity */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                18. Indemnity
              </h2>
              <p className="text-gray-700 mb-2">
                The Client shall hold BlueCMR, its directors, employees and collaborators harmless against any claims, actions, penalties, damages, losses, and reasonable costs and expenses arising from:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>a breach by the Client or its Authorised Users of these Terms or of applicable regulations;</li>
                <li>the Client Content;</li>
                <li>the lack of entitlement or authorisation of signatories;</li>
                <li>the unlawful or unauthorised use of the Platform;</li>
                <li>third-party claims relating to documents, data, instructions or actions attributable to the Client.</li>
              </ul>
              <p className="text-gray-700">
                The foregoing shall not apply to the extent that the claim arises directly from a breach attributable to BlueCMR.
              </p>
            </section>

            {/* 19. Term and termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                19. Term and termination
              </h2>
              <p className="text-gray-700 mb-4">
                These Terms shall remain in force for as long as the Client or its Authorised Users use the Platform or maintain an active contractual relationship with BlueCMR.
              </p>
              <p className="text-gray-700 mb-4">
                The Client may stop using the Platform and request the closure of its account in accordance with the procedures made available by BlueCMR, without prejudice to any outstanding payment obligations and to any liabilities arising from prior actions.
              </p>
              <p className="text-gray-700 mb-2">BlueCMR may terminate, for cause or otherwise, the contractual relationship or access to the Platform in the event of:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>a serious breach of these Terms;</li>
                <li>persistent non-payment;</li>
                <li>fraudulent, unlawful or insecure use;</li>
                <li>a legal requirement;</li>
                <li>discontinuation of the Service by business decision, in which case BlueCMR will give reasonable advance notice.</li>
              </ul>
              <p className="text-gray-700">
                Upon termination, those provisions which by their nature are intended to survive shall remain in force, including those relating to intellectual property, confidentiality, limitation of liability, indemnity, data protection, outstanding payments and jurisdiction.
              </p>
            </section>

            {/* 20. Data export and retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                20. Data export and retention
              </h2>
              <p className="text-gray-700 mb-4">
                During the term of the Service and, following termination, for such reasonable period as may be specified contractually or by BlueCMR, the Client may request the reasonable export or retrieval of its information in the standard formats available.
              </p>
              <p className="text-gray-700">
                BlueCMR may delete or anonymise Client data once the relationship has ended, except where there is a legal retention obligation, a need to keep residual backup copies, or a justified need to retain evidence, logs or records required for regulatory compliance, the defence of claims or the security of the service.
              </p>
            </section>

            {/* 21. Amendment of the Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                21. Amendment of the Terms
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR may amend these Terms for legal, regulatory, technical, operational or commercial reasons.
              </p>
              <p className="text-gray-700 mb-4">
                Where the changes are significant, BlueCMR will give notice of them by reasonable means, including through the Platform or the email address associated with the account. Unless otherwise indicated, amendments shall take effect on the date stated in the update itself.
              </p>
              <p className="text-gray-700">
                Continued use of the Platform after the new version comes into force shall constitute acceptance of it, without prejudice to the Client's right to stop using the Service if it does not agree with the amendment.
              </p>
            </section>

            {/* 22. Communications */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                22. Communications
              </h2>
              <p className="text-gray-700 mb-4">
                Communications between BlueCMR and the Client may be made by electronic means, including email, notices within the Platform or notifications in the relevant account.
              </p>
              <p className="text-gray-700 mb-4">
                The Client acknowledges the contractual validity of such electronic communications.
              </p>
              <p className="text-gray-700">
                For the purpose of contacting BlueCMR, the address <strong>support@bluecmr.com</strong> may be used, or any other address or channel validly notified by BlueCMR.
              </p>
            </section>

            {/* 23. Force majeure */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                23. Force majeure
              </h2>
              <p className="text-gray-700">
                BlueCMR shall not be liable for delays or failures to perform due to causes beyond its reasonable control, including failures of third-party providers, supply outages, widespread attacks, labour disputes, natural disasters, acts of public authorities, war, civil unrest, pandemics or telecommunications incidents.
              </p>
            </section>

            {/* 24. Assignment */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                24. Assignment
              </h2>
              <p className="text-gray-700 mb-4">
                The Client may not assign its contractual position or its rights or obligations under these Terms without the prior written authorisation of BlueCMR, except in cases of internal reorganisation or universal transfer of assets and liabilities that do not materially prejudice BlueCMR.
              </p>
              <p className="text-gray-700">
                BlueCMR may assign or transfer these Terms or the contractual relationship in the context of a corporate restructuring, a transfer of business or an equivalent corporate transaction, giving notice where reasonable.
              </p>
            </section>

            {/* 25. Severability and no waiver */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                25. Severability and no waiver
              </h2>
              <p className="text-gray-700 mb-4">
                The nullity, invalidity or unenforceability of any provision of these Terms shall not affect the validity of the remainder.
              </p>
              <p className="text-gray-700">
                Failure by BlueCMR to exercise any right or power shall not constitute a waiver thereof.
              </p>
            </section>

            {/* 26. Governing law and jurisdiction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                26. Governing law and jurisdiction
              </h2>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by Spanish law.
              </p>
              <p className="text-gray-700 mb-4">
                To the extent permitted by applicable regulations, any dispute arising from these Terms or from the use of the Platform shall be submitted to the Courts and Tribunals of León, Spain, with express waiver of any other jurisdiction that might otherwise apply.
              </p>
              <p className="text-gray-700">
                Where mandatory consumer protection legislation or mandatory rules on jurisdiction apply, the provisions of such legislation shall prevail.
              </p>
            </section>

            {/* 27. Order of precedence of documents */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                27. Order of precedence of documents
              </h2>
              <p className="text-gray-700 mb-2">
                Unless expressly stated otherwise, the order of precedence among the applicable contractual documentation shall be as follows:
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                <li>subscription agreement, order, accepted proposal or signed special terms and conditions;</li>
                <li>financial or functional annexes;</li>
                <li>DPA;</li>
                <li>these Terms;</li>
                <li>Privacy Policy;</li>
                <li>any other policy or ancillary document.</li>
              </ol>
            </section>

            {/* 28. Entire agreement */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                28. Entire agreement
              </h2>
              <p className="text-gray-700">
                These Terms, together with the applicable contractual documentation, constitute the entire agreement between the parties in relation to access to and use of the Platform, and supersede any prior agreements, communications or understandings regarding its subject matter.
              </p>
            </section>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                © 2026 BlueCMR S.L.U. All rights reserved.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <strong>Contact:</strong> For any question or enquiry regarding these Terms, you may contact us at <strong>support@bluecmr.com</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsPageEn;
